import type { Currency } from '@ps/web-core-enums';
import type { Error as ValidationError } from '@ps/web-shared-validators';

import type { StatusCode } from '@swf/core/enums/statusCode';

export const LIMIT_UNSET = -1;

export const MIN_DEPOSIT_LIMIT_DEFAULT_VALUE = 1;

export const MAX_DEPOSIT_LIMIT_DEFAULT_VALUE = 1000000;

export const TIME_BETWEEN_LOGINS_LIMIT_TYPE = 29;

export const SEVEN_DAYS = 7;

export const CH_INFO_EMAIL = 'info@casino777.ch';

export enum AccumulatedLossLimitType {
  Daily = 17,
  Weekly = 30,
  Monthly = 18
}

export enum NetDepositLimitType {
  Daily = 'daily',
  Monthly = 'monthly',
  Budget = 'budget'
}

export enum FAQType {
  NetDeposit = 'net-deposit',
  SpendBudget = 'spend-budget'
}

export enum LimitType {
  Daily = 6,
  Weekly = 26,
  Monthly = 27
}

export enum SpendLimitType {
  Daily = 7,
  Weekly = 8,
  Monthly = 9
}

export enum RestrictDepositLimitType {
  Daily = 1,
  Weekly = 7,
  Monthly = 30
}

export interface BaseGameLimits {
  [key: string]: number;
}

export interface BaseLimits {
  currencies?: string[];
  hadLimitUk?: boolean;
  limits: LimitCriteria[];
}

export interface ConversionRate {
  currency: Currency;
  rate: number;
  inversedRate: number;
}

export interface DepositLimitCriteria {
  inEffectUntil: string | null;
  limit: number;
  limitMax: number;
  limitMin: number;
  regulatoryLimit: number;
}

export interface DepositLimitProps {
  minLimit: number;
  maxLimit: number;
  disableLimits?: boolean;
  monthlyFieldRequired?: boolean;
  conversionRates?: ConversionRate[];
  removeLimitOption?: RemoveLimitOptions;
  formatCurrentLimit(value: number): string | undefined;
}

export interface DiagramSummaryProps {
  moneyIn: number;
  moneyOut: number;
  remainingBalance: number;
  limit: number;
  startDate: number;
  endDate: number;
  variant: NetDepositLimitType;
}

export interface DepositLimitValues {
  previousDailyLimit: number;
  previousWeeklyLimit: number;
  previousMonthlyLimit: number;
  dailyLimit?: number;
  weeklyLimit?: number;
  monthlyLimit?: number;
}

export interface NetDepositLimitValues {
  limitDaily: number;
  moneyInDaily: number;
  moneyOutDaily: number;
  remainingBalanceDaily: number;
  startDateDaily: number;
  endDateDaily: number;
  moneyInMonthly: number;
  moneyOutMonthly: number;
  remainingBalanceMonthly: number;
  limitMonthly: number;
  startDateMonthly: number;
  endDateMonthly: number;
}

export interface ExcludePlayerResponse extends LimitResponse {
  until?: string;
  revocationTimeUntil?: number;
  limit?: number;
  daysExcluded: number;
  nationalExclude?: number;
  daysRequested?: number;
  statusCode?: StatusCode;
}

export enum ExclusionTime {
  Indefinite = 0,
  HalfDay = -12,
  TwentyFourHours = -24,
  OneDay = 1,
  ThreeDays = 3,
  SevenDays = 7,
  ThirtyDays = 30,
  FortyTwoDays = 42,
  SixtyDays = 60,
  NinetyDays = 90,
  HundredTwentyDays = 120,
  HundredEightyDays = 180,
  TwelveMonths = 12,
  OneYear = 365,
  TwoYears = 365 * 2,
  ThreeYears = 365 * 3,
  FourYears = 365 * 4,
  FiveYears = 365 * 5
}

export interface LimitCriteria {
  type: number;
  amount: number;
  currency?: Currency;
  password?: string;
}

export interface LimitFormValue {
  days?: string;
  hours?: string;
  minutes?: string;
}

export interface LimitResponse {
  type?: number;
  errorCode?: number;
  errorMsg?: string;
  inEffect?: boolean;
  hours?: number;
  effectiveNowWeekly?: boolean;
  limitCoolOffTime?: number;
  errorCodeWeekly?: number;
  limitAmount?: number;
  currentLimit?: number;
  newLimit?: number;
  limitChangeResult?: number;
}

export interface Limits extends BaseLimits {
  hours: number;
}

export interface SportLimits {
  type: number;
  amount: number;
  currency?: Currency;
  hours: number;
}

export type RemoveLimitOptions = 'checkboxes' | 'radioButtons' | 'rawToggle';
export type DepositLimitType = 'daily' | 'weekly' | 'monthly';

export type ValidatorFunction = (
  compareValue: number,
  message: ValidationError['message'],
  allowEmpty: boolean
) => (value: number) => ValidationError;
